// Port of `TextCleanup.quotations`/`curled`/`lines` (SuiteModel/TextCleanup.swift): the one quotation detector that Clean
// Up, Statistics' dialogue card, Shape and the style linter share. Works on curled text, paragraph by paragraph;
// positions are Unicode scalars. Exact.
import { fixedQuoteScalars, ELISIONS, QUOTE_MARKERS, QUOTE_OPENERS } from './quoteMarks.ts';
import { fromScalars, isAlphabetic, isDigit, isWhitespace } from './scalars.ts';

export interface Quotation {
  double: boolean;
  open: number;
  close: number | null;
  /** Where it stops holding what follows: its closing mark, or its paragraph's end. */
  end: number;
  /** How many quotations it sits inside. */
  depth: number;
  /** Its paragraph [from, to). */
  line: [number, number];
}

/** `TextCleanup.lines`: each line's [from, to), the newline left out. */
export function scalarLines(s: readonly string[]): [number, number][] {
  const out: [number, number][] = [];
  let start = 0;
  s.forEach((c, i) => {
    if (c === '\n') {
      out.push([start, i]);
      start = i + 1;
    }
  });
  out.push([start, s.length]);
  return out;
}

/** `TextCleanup.curled`: quotation marks curled and turned (scalar for scalar, so offsets are unchanged). */
export const curled = (s: string[]): string[] => fixedQuoteScalars(s);

/** Punctuation a closing single quote follows, as in `‘Yes,’` (an apostrophe follows letters). */
export const CLOSING_PUNCTUATION: ReadonlySet<string> = new Set(['.', ',', '!', '?', ';', ':', '—', '–', '-', '…', ')', ']']);

/** Whether a word often ends in an apostrophe: plural possessives (`dogs’`, `Marx’`), dropped g's (`goin’`), and
 *  `o’`, `an’`, `’n’`, `th’`. */
export function mayEndInApostrophe(word: string): boolean {
  const w = word.toLowerCase();
  return w.endsWith('s') || w.endsWith('x') || w.endsWith('z') || w.endsWith('in') || ['o', 'an', 'n', 'th'].includes(w);
}

/** `TextCleanup.quotations`: the quotations in curled text, paragraph by paragraph. A single quote's close is told from
 *  apostrophes by what's around it: the first ’ after punctuation (`‘Yes,’`), or after a word that doesn't usually end
 *  in an apostrophe (`‘Cherry Bomb’ and`), closes it; failing that, the last ’ that could. */
export function quotations(c: readonly string[]): Quotation[] {
  const all: Quotation[] = [];
  for (const line of scalarLines(c)) {
    const found: Quotation[] = [];
    let openDouble: number | null = null;
    const consumed = new Set<number>();
    const previous = (i: number) => {
      let j = i - 1;
      while (j >= line[0] && QUOTE_MARKERS.has(c[j])) j--;
      return j >= line[0] ? c[j] : undefined;
    };
    const next = (i: number) => {
      let j = i + 1;
      while (j < line[1] && QUOTE_MARKERS.has(c[j])) j++;
      return j < line[1] ? c[j] : undefined;
    };
    const isWordChar = (x: string | undefined) => x !== undefined && (isAlphabetic(x) || isDigit(x));
    const opensSingle = (i: number): boolean => {
      if (c[i] !== '‘') return false;
      const before = previous(i);
      if (before !== undefined && !isWhitespace(before) && !QUOTE_OPENERS.has(before)) return false;
      // An elision ('til, '70s) is an apostrophe, whichever way it was curled; `‘E’` is a quote.
      if (isDigit(next(i))) return false;
      let word = '';
      let j = i + 1;
      while (j < line[1] && isAlphabetic(c[j])) word += c[j++];
      return !ELISIONS.has(word.toLowerCase()) || (j < line[1] && c[j] === '’');
    };
    for (let i = line[0]; i < line[1]; i++) {
      const ch = c[i];
      if (ch === '“') {
        if (openDouble !== null) found[openDouble].end = i; // never closed; this starts afresh
        found.push({ double: true, open: i, close: null, end: line[1], depth: 0, line });
        openDouble = found.length - 1;
      } else if (ch === '”') {
        if (openDouble !== null) {
          found[openDouble].close = i;
          found[openDouble].end = i + 1;
          openDouble = null;
        }
      } else if (ch === '‘' && opensSingle(i)) {
        let limit = line[1];
        for (let k = i + 1; k < line[1]; k++) if (opensSingle(k)) { limit = k; break; }
        if (openDouble !== null) {
          for (let k = i + 1; k < limit; k++) if (c[k] === '”') { limit = k; break; }
        }
        let best: number | null = null, fallback: number | null = null;
        for (let j = i + 1; j < Math.max(i + 1, limit); j++) {
          if (!(c[j] === '’' && !consumed.has(j) && !isWordChar(next(j)))) continue;
          const prev = previous(j);
          if (prev !== undefined && CLOSING_PUNCTUATION.has(prev)) { best = j; break; }
          let start = j;
          while (start > i + 1 && isAlphabetic(c[start - 1])) start--;
          const word = fromScalars(c, start, j);
          if (word !== '' && !mayEndInApostrophe(word)) { best = j; break; }
          fallback = j;
        }
        const close = best ?? fallback;
        if (close !== null) consumed.add(close);
        found.push({ double: false, open: i, close, end: close !== null ? close + 1 : line[1], depth: 0, line });
      }
    }
    for (const q of found) q.depth = found.filter((o) => o.open < q.open && o.end > q.open).length;
    all.push(...found);
  }
  return all;
}
